import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ChatScreen.css';
import { doctorTypes, SET_CONTEXT_URL, SEND_MESSAGE_URL, GET_RESULT_URL } from '../constants';

const ChatScreen = () => {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const contextIdParam = searchParams.get('contextId') || 'unknown';
  const text = doctorTypes[contextIdParam] || 'unknown';

  useEffect(() => {
    // Set context on backend
    const setContext = async () => {
      try {
        await fetch(SET_CONTEXT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ contextId: contextIdParam }),
        });
      } catch (error) {
        console.error('Error setting context:', error);
      }
    };

    setContext();

    // Add welcome message
    setMessages([
      {
        id: 1,
        text: `Добро пожаловать! Вы выбрали тренажер: ${text}. Как я могу вам помочь?`,
        isUser: false,
        timestamp: new Date(),
      },
    ]);
  }, [text, contextIdParam]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Send message to backend
      const response = await fetch(SEND_MESSAGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: inputMessage }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data);
      const botMessage = {
        id: Date.now() + 1,
        text: data.reply || 'Получен ответ от сервера',
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Извините, произошла ошибка при отправке сообщения. Попробуйте еще раз.',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEvaluateTraining = async () => {
    setIsLoading(true);

    try {
      const response = await fetch(GET_RESULT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const resultMessage = {
        id: Date.now(),
        text: data.reply || data.message || 'Результаты получены',
        isUser: false,
        timestamp: new Date(),
        isResult: true, // Flag to identify result messages that need HTML rendering
      };

      setMessages((prev) => [...prev, resultMessage]);
    } catch (error) {
      console.error('Error getting results:', error);
      const errorMessage = {
        id: Date.now(),
        text: 'Извините, произошла ошибка при получении результатов. Попробуйте еще раз.',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  function lockViewportHeight() {
    const h = document.documentElement.clientHeight;
    document.body.style.height = `${h}px`;
    document.body.style.overflow = 'hidden';
  }

  function unlockViewportHeight() {
    document.body.style.height = '';
    document.body.style.overflow = '';
  }

  window.addEventListener('focusin', lockViewportHeight);
  window.addEventListener('focusout', unlockViewportHeight);

  return (
    <div className='chat-screen'>
      <div className='chat-header'>
        <h2 className='chat-title'>{text}</h2>
        <div className='header-spacer'></div>
        <button onClick={handleEvaluateTraining} disabled={isLoading} className='button'>
          Оценить тренировку
        </button>
      </div>

      <div className='chat-container'>
        <div className='messages-container'>
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.isUser ? 'user-message' : 'bot-message'}`}>
              <div className='message-content'>
                {message.isResult ? (
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{message.text}</pre>
                ) : (
                  message.text
                )}
              </div>
              <div className='message-time'>
                {message.timestamp.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className='message bot-message'>
              <div className='message-content'>
                <div className='typing-indicator'>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className='input-container'>
          <div className='input-wrapper'>
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder='Введите ваше сообщение...'
              className='message-input'
              rows='1'
              disabled={isLoading}
            />
            <button onClick={handleSendMessage} disabled={!inputMessage.trim() || isLoading} className='button'>
              Отправить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatScreen;
